/**
 * AI Agents V3 Phase 6 WP10 task 5 (§1ch, L11): a workspace recipient opens the Task Center item
 * link from an email (`/<tenant>/cloudautomation/taskcenter?task=<guid>&ws=<workspace>`) while
 * signed out. Sign-in returns to the site root (redirectUri = origin), which drops the item, so the
 * link is kept in sessionStorage before sign-in and opened once the profile is loaded.
 */
export const TASK_DEEP_LINK_KEY = 'automatr.task_deep_link';

/** The public external answer page: no sign-in, no tenant redirect. */
export const AGENT_ANSWER_PATH = '/agent-answer';

const TASK_LINK = /^\/[^/?#]+\/cloudautomation\/taskcenter\/?$/;

/** The path of a URL without query or fragment. */
export function pathOf(url: string): string {
  return (url || '').split('?')[0].split('#')[0];
}

export function isAgentAnswerPath(url: string): boolean {
  return pathOf(url) === AGENT_ANSWER_PATH;
}

/** True for a Task Center item link (the path plus a task= query). */
export function isTaskDeepLink(pathAndQuery: string): boolean {
  if (!pathAndQuery || !TASK_LINK.test(pathOf(pathAndQuery))) return false;
  const query = pathAndQuery.indexOf('?') >= 0 ? pathAndQuery.substring(pathAndQuery.indexOf('?') + 1).split('#')[0] : '';
  return query.split('&').some(p => p.startsWith('task=') && p.length > 'task='.length);
}

/** Keeps the item link (path + query) for after sign-in. Storage failures are ignored. */
export function rememberTaskDeepLink(pathAndQuery: string, storage: Storage = sessionStorage): boolean {
  if (!isTaskDeepLink(pathAndQuery)) return false;
  try {
    storage.setItem(TASK_DEEP_LINK_KEY, pathAndQuery);
    return true;
  } catch {
    return false;
  }
}

/**
 * Takes (and forgets) the kept link when it belongs to the signed-in tenant; null otherwise. A link
 * for another tenant is dropped: the person is not signed in there.
 */
export function takeTaskDeepLink(tenantUniqueKey: string, storage: Storage = sessionStorage): string | null {
  let link: string | null = null;
  try {
    link = storage.getItem(TASK_DEEP_LINK_KEY);
    if (link !== null) storage.removeItem(TASK_DEEP_LINK_KEY);
  } catch {
    return null;
  }
  if (!link || !tenantUniqueKey || !isTaskDeepLink(link)) return null;
  return link.startsWith('/' + tenantUniqueKey + '/') ? link : null;
}

// Any page of the signed-in app, not only Task Center items: a link opened in a tab that is not
// signed in (a new tab, a bookmark, a pasted link, an expired session) kept the person out of the
// page they asked for and dropped them on the dashboard after sign-in. Same key, same rules.

/** `/<tenant>/cloudautomation/<page>...`; the bare tenant home is where sign-in lands anyway. */
const PAGE_LINK = /^\/[^/?#]+\/cloudautomation\/[^/?#]/;

/** True for a page of the signed-in app (any `/<tenant>/cloudautomation/<page>` link). */
export function isPageDeepLink(pathAndQuery: string): boolean {
  return !!pathAndQuery && PAGE_LINK.test(pathOf(pathAndQuery));
}

/** Keeps the page link (path + query) for after sign-in. Storage failures are ignored. */
export function rememberPageDeepLink(pathAndQuery: string, storage: Storage = sessionStorage): boolean {
  if (!isPageDeepLink(pathAndQuery)) return false;
  try {
    storage.setItem(TASK_DEEP_LINK_KEY, pathAndQuery);
    return true;
  } catch {
    return false;
  }
}

/** As takeTaskDeepLink, for any kept page link: once, and only for the signed-in tenant. */
export function takePageDeepLink(tenantUniqueKey: string, storage: Storage = sessionStorage): string | null {
  let link: string | null = null;
  try {
    link = storage.getItem(TASK_DEEP_LINK_KEY);
    if (link !== null) storage.removeItem(TASK_DEEP_LINK_KEY);
  } catch {
    return null;
  }
  if (!link || !tenantUniqueKey || !isPageDeepLink(link)) return null;
  return link.startsWith('/' + tenantUniqueKey + '/') ? link : null;
}
